import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi, publicVideoUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Film, Lock, Play, Loader2, AlertCircle, Clapperboard, List, ThumbsUp, MessageSquare, Maximize2 } from 'lucide-react';

export default function PublicShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [content, setContent] = useState(null);
  const [mode, setMode] = useState('stitched'); // 'stitched' | 'scenes'
  const [sceneIdx, setSceneIdx] = useState(0);
  const [usedPw, setUsedPw] = useState('');
  const [feedbackList, setFeedbackList] = useState([]);
  const [fbName, setFbName] = useState('');
  const [fbMessage, setFbMessage] = useState('');
  const [submittingFb, setSubmittingFb] = useState(false);
  const [fbDone, setFbDone] = useState(null);
  const [pinScene, setPinScene] = useState(false);
  const sceneVideoRef = useRef(null);
  const stitchedVideoRef = useRef(null);
  const wasFsRef = useRef(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  const isFs = () => document.fullscreenElement || document.webkitFullscreenElement;
  const enterFs = (v) => {
    const req = v.requestFullscreen || v.webkitRequestFullscreen || v.webkitEnterFullscreen;
    if (req) { try { req.call(v); } catch { /* needs gesture on some browsers */ } }
  };
  const playAllFullscreen = () => {
    const stitchedMode = mode === 'stitched' && content?.stitched?.available;
    const v = stitchedMode ? stitchedVideoRef.current : sceneVideoRef.current;
    if (!v) return;
    if (!stitchedMode && sceneIdx !== 0) setSceneIdx(0);
    const p = v.play(); if (p && p.catch) p.catch(() => {});
    setAutoplayBlocked(false);
    enterFs(v);
  };
  const advanceScene = () => {
    wasFsRef.current = !!isFs();
    setSceneIdx((i) => (content && i < content.scenes.length - 1 ? i + 1 : i));
  };
  // Autoplay each scene (inherits the user's earlier play gesture) and, if the
  // previous scene was watched fullscreen, continue the next one fullscreen too.
  useEffect(() => {
    const v = sceneVideoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.then) p.then(() => setAutoplayBlocked(false)).catch(() => setAutoplayBlocked(true));
    const onPlaying = () => {
      if (wasFsRef.current && !isFs()) {
        const req = v.requestFullscreen || v.webkitRequestFullscreen || v.webkitEnterFullscreen;
        if (req) { try { req.call(v); } catch { /* fullscreen needs a gesture on some browsers */ } }
      }
      wasFsRef.current = false;
    };
    v.addEventListener('playing', onPlaying);
    return () => v.removeEventListener('playing', onPlaying);
  }, [sceneIdx, content]);

  // Detect blocked autoplay on the stitched player so we can show a "Tap to start" hint.
  useEffect(() => {
    if (mode !== 'stitched' || !content?.stitched?.available) return;
    const v = stitchedVideoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.then) p.then(() => setAutoplayBlocked(false)).catch(() => setAutoplayBlocked(true));
  }, [mode, content]);

  const loadFeedback = async (pw) => {
    try {
      const res = await publicApi.get(`/public/share/${token}/feedback`, { params: { password: pw || undefined } });
      setFeedbackList(res.data.items || []);
    } catch { /* ignore */ }
  };

  const submitFeedback = async (decision) => {
    if (decision === 'comment' && !fbMessage.trim()) return;
    setSubmittingFb(true);
    try {
      const scene_id = (pinScene && (mode === 'scenes' || !content?.stitched?.available)) ? content?.scenes?.[sceneIdx]?.id : undefined;
      await publicApi.post(`/public/share/${token}/feedback`, {
        client_name: fbName, message: fbMessage, decision, password: usedPw || undefined, scene_id,
      });
      setFbMessage('');
      setFbDone(decision);
      await loadFeedback(usedPw);
      setTimeout(() => setFbDone(null), 4000);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? 'This link now requires a password — please reload.' : 'Could not send your feedback. Please try again.');
    }
    finally { setSubmittingFb(false); }
  };

  const loadMeta = useCallback(async () => {
    try {
      const res = await publicApi.get(`/public/share/${token}`);
      setMeta(res.data);
      if (!res.data.requires_password) {
        await unlock('');
      }
    } catch (e) {
      setError(e?.response?.status === 404 ? 'This share link is unavailable or has been turned off.' : 'Failed to load this link.');
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async (pw) => {
    setUnlocking(true);
    try {
      const res = await publicApi.post(`/public/share/${token}/content`, { password: pw });
      setContent(res.data);
      setUsedPw(pw || '');
      setError(null);
      setMode(res.data.stitched?.available ? 'stitched' : 'scenes');
      loadFeedback(pw);
    } catch (e) {
      if (e?.response?.status === 401) setError('Incorrect password. Please try again.');
      else setError('Failed to load this link.');
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const isVertical = (content?.aspect_ratio || meta?.aspect_ratio) === '9:16';
  const brandName = content?.brand_name || meta?.brand_name;
  const brandLogo = content?.brand_logo || meta?.brand_logo;
  const displayTitle = content?.share_title || content?.project_name;
  const gateTitle = meta?.share_title || meta?.project_name;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060608] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#FF4500] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060608] text-white" data-testid="public-share-page">
      {/* Header */}
      <header className="border-b border-white/[0.06] px-6 py-4 flex items-center gap-2" data-testid="public-brand-header">
        {brandLogo ? (
          <img src={brandLogo} alt={brandName || 'logo'} data-testid="public-brand-logo" className="h-7 w-auto max-w-[160px] object-contain" />
        ) : (
          <div className="w-7 h-7 rounded-lg bg-[#FF4500] flex items-center justify-center"><Film className="w-4 h-4 text-white" /></div>
        )}
        <span data-testid="public-brand-name" className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{brandName || 'AvantGarde Media'}</span>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {error && !content && (
          <div data-testid="public-error" className="flex items-start gap-3 p-5 rounded-xl bg-red-500/5 border border-red-500/20 mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* Password gate */}
        {meta && meta.requires_password && !content && (
          <div className="max-w-sm mx-auto text-center py-16" data-testid="public-password-gate">
            <div className="w-14 h-14 rounded-full bg-[#FF4500]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-[#FF4500]" />
            </div>
            <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{gateTitle}</h1>
            <p className="text-sm text-[#8A8A93] mb-6">This share is password protected.</p>
            <form onSubmit={(e) => { e.preventDefault(); unlock(password); }}>
              <input
                data-testid="public-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-2.5 rounded-lg bg-[#121216] border border-white/10 text-sm text-white focus:border-[#FF4500]/50 outline-none mb-3"
              />
              <button data-testid="public-unlock-btn" type="submit" disabled={unlocking || !password}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#FF4500] text-white hover:bg-[#FF4500]/90 disabled:opacity-40">
                {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Unlock
              </button>
            </form>
          </div>
        )}

        {/* Content */}
        {content && (
          <div data-testid="public-content">
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
              <div>
                <h1 data-testid="public-share-title" className="text-2xl sm:text-3xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>{displayTitle}</h1>
                <p className="text-sm text-[#8A8A93] mt-1">{content.scenes.length} scene{content.scenes.length !== 1 ? 's' : ''}</p>
              </div>
              {content.stitched?.available && content.scenes.length > 0 && (
                <div className="flex gap-1 bg-[#121216] rounded-lg p-1 border border-white/[0.06]">
                  <button data-testid="public-mode-stitched" onClick={() => setMode('stitched')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${mode === 'stitched' ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93]'}`}>
                    <Clapperboard className="w-3.5 h-3.5" /> Final Cut
                  </button>
                  <button data-testid="public-mode-scenes" onClick={() => setMode('scenes')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${mode === 'scenes' ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93]'}`}>
                    <List className="w-3.5 h-3.5" /> Scenes
                  </button>
                </div>
              )}
            </div>

            {(content.stitched?.available || content.scenes.length > 0) && (
              <button data-testid="play-all-fullscreen-btn" onClick={playAllFullscreen}
                className="w-full sm:w-auto mb-4 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold bg-[#FF4500] text-white hover:bg-[#FF4500]/90 transition-colors">
                <Maximize2 className="w-4 h-4" /> Play all fullscreen
              </button>
            )}

            {content.scenes.length === 0 && !content.stitched?.available && (
              <div className="text-center py-20 text-[#55555C]">
                <Film className="w-8 h-8 mx-auto mb-3" />
                <p className="text-sm">No videos have been generated for this project yet.</p>
              </div>
            )}

            {/* Stitched player */}
            {mode === 'stitched' && content.stitched?.available && (
              <div className={`relative mx-auto bg-black rounded-xl overflow-hidden border border-white/[0.06] ${isVertical ? 'max-w-sm' : ''}`} data-testid="public-stitched-player">
                <video ref={stitchedVideoRef} controls autoPlay playsInline className="w-full" src={publicVideoUrl(token, content.stitched.path, content.access_key)} />
                {autoplayBlocked && (
                  <button data-testid="autoplay-hint-stitched" onClick={() => { const v = stitchedVideoRef.current; if (v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } setAutoplayBlocked(false); }}
                    className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm text-white gap-2">
                    <span className="w-14 h-14 rounded-full bg-[#FF4500] flex items-center justify-center"><Play className="w-6 h-6 ml-0.5" /></span>
                    <span className="text-sm font-medium">Tap to start</span>
                  </button>
                )}
              </div>
            )}

            {/* Scene-by-scene player */}
            {(mode === 'scenes' || !content.stitched?.available) && content.scenes.length > 0 && (
              <div data-testid="public-scenes-player">
                <div className={`relative mx-auto bg-black rounded-xl overflow-hidden border border-white/[0.06] ${isVertical ? 'max-w-sm' : ''}`}>
                  <video
                    ref={sceneVideoRef}
                    controls
                    autoPlay
                    playsInline
                    className="w-full"
                    src={publicVideoUrl(token, content.scenes[sceneIdx]?.path, content.access_key)}
                    onEnded={advanceScene}
                  />
                  {autoplayBlocked && (
                    <button data-testid="autoplay-hint-scenes" onClick={() => { const v = sceneVideoRef.current; if (v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } setAutoplayBlocked(false); }}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm text-white gap-2">
                      <span className="w-14 h-14 rounded-full bg-[#FF4500] flex items-center justify-center"><Play className="w-6 h-6 ml-0.5" /></span>
                      <span className="text-sm font-medium">Tap to start</span>
                    </button>
                  )}
                </div>
                <p className="text-center text-sm text-[#8A8A93] mt-3">
                  Scene {sceneIdx + 1} of {content.scenes.length}: <span className="text-white">{content.scenes[sceneIdx]?.title}</span>
                </p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  {content.scenes.map((s, i) => (
                    <button key={s.id} data-testid={`public-scene-chip-${i}`} onClick={() => setSceneIdx(i)}
                      className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 border transition-all ${i === sceneIdx ? 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}>
                      <Play className="w-3 h-3" /> {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Client feedback */}
            <div className="mt-10 pt-8 border-t border-white/[0.06]" data-testid="public-feedback-section">
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Your feedback</h2>
              <p className="text-xs text-[#8A8A93] mb-4">Approve this cut or request changes — the team will be notified.</p>

              {fbDone && (
                <div data-testid="public-feedback-done" className="mb-4 p-3 rounded-lg bg-[#10B981]/[0.08] border border-[#10B981]/25 text-sm text-[#10B981]">
                  Thanks! Your {fbDone === 'approve' ? 'approval' : fbDone === 'request_changes' ? 'change request' : 'comment'} was sent.
                </div>
              )}

              <div className="space-y-3 max-w-xl">
                <input data-testid="feedback-name-input" value={fbName} onChange={e => setFbName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full px-3 py-2 rounded-lg bg-[#121216] border border-white/10 text-sm text-white outline-none focus:border-[#FF4500]/40" />
                <textarea data-testid="feedback-message-input" value={fbMessage} onChange={e => setFbMessage(e.target.value)}
                  rows={3} placeholder="Add a note or the changes you'd like…"
                  className="w-full px-3 py-2 rounded-lg bg-[#121216] border border-white/10 text-sm text-white outline-none focus:border-[#FF4500]/40 resize-none" />
                {(mode === 'scenes' || !content?.stitched?.available) && content.scenes.length > 0 && (
                  <label data-testid="feedback-pin-toggle" className="flex items-center gap-2 text-xs text-[#8A8A93] cursor-pointer select-none">
                    <input type="checkbox" checked={pinScene} onChange={e => setPinScene(e.target.checked)} className="accent-[#FF4500]" />
                    Pin this feedback to <span className="text-white">Scene {sceneIdx + 1}: {content.scenes[sceneIdx]?.title}</span>
                  </label>
                )}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  <button data-testid="feedback-approve-btn" onClick={() => submitFeedback('approve')} disabled={submittingFb}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg text-sm font-semibold bg-[#10B981] text-white hover:bg-[#10B981]/90 disabled:opacity-40">
                    <ThumbsUp className="w-4 h-4" /> Approve
                  </button>
                  <button data-testid="feedback-changes-btn" onClick={() => submitFeedback('request_changes')} disabled={submittingFb}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg text-sm font-semibold bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/25 disabled:opacity-40">
                    <MessageSquare className="w-4 h-4" /> Request Changes
                  </button>
                  <button data-testid="feedback-comment-btn" onClick={() => submitFeedback('comment')} disabled={submittingFb || !fbMessage.trim()}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-lg text-sm font-medium bg-white/[0.05] text-white border border-white/10 hover:bg-white/10 disabled:opacity-40">
                    {submittingFb ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} Comment
                  </button>
                </div>
              </div>

              {feedbackList.length > 0 && (
                <div className="mt-6 space-y-3 max-w-xl" data-testid="public-feedback-list">
                  {feedbackList.map(f => (
                    <div key={f.id} className={`p-3 rounded-lg border ${f.resolved ? 'bg-[#10B981]/[0.04] border-[#10B981]/20' : 'bg-white/[0.02] border-white/[0.06]'}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-white">{f.client_name}</span>
                        {f.decision === 'approve' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">Approved</span>}
                        {f.decision === 'request_changes' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">Changes requested</span>}
                        {f.scene_title && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF]">📌 {f.scene_title}</span>}
                        {f.resolved && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">Resolved</span>}
                        <span className="text-[10px] text-[#55555C] ml-auto">{new Date(f.created_at).toLocaleString()}</span>
                      </div>
                      {f.message && <p className="text-sm text-[#C7C7CF] whitespace-pre-wrap break-words">{f.message}</p>}
                      {f.reply && (
                        <div className="mt-2 pl-3 border-l-2 border-[#FF4500]/40" data-testid="public-feedback-reply">
                          <p className="text-[10px] text-[#FF4500] font-semibold mb-0.5">Team reply</p>
                          <p className="text-xs text-[#C7C7CF] whitespace-pre-wrap break-words">{f.reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
